/**
 * One gym day in the list.
 *
 * Collapsed it answers "did that get logged, and how big was it": the date, the counts,
 * the score, and a bar for comparison against the rest of the screen. Expanded it shows
 * every exercise and every set, in the same notation the bot replies with (core's
 * `formatSets`), so the two can be read against each other without translation.
 *
 * The open/closed state used to be signalled only by a hover colour change on the date,
 * which does not exist on a phone — so on the device this is actually used on, an
 * expanded row and a collapsed row were identical. It now carries a chevron that
 * rotates, which works on touch and reads without colour.
 *
 * Read-only, deliberately — B-14 is the editor, and shipping the list on its own is why
 * this screen exists at all (D-57).
 */

import { Link } from "react-router-dom";
import type { Workout } from "@altrtrack/core";
import { exerciseById, formatNumber, formatSets, plural, scoreWorkout } from "@altrtrack/core";
import { relativeDay } from "../relative.js";

export function DayRow({
  workout,
  score,
  share,
  today,
  open,
  onToggle,
  /** Highlighted because the chart bar above stands for this day. Presentational only. */
  lit = false,
}: {
  workout: Workout;
  score: number;
  /** This day's score as a fraction of the best day on screen. Sizes the bar. */
  share: number;
  today: string;
  open: boolean;
  onToggle: () => void;
  lit?: boolean;
}) {
  const sets = workout.exercises.reduce((n, e) => n + e.sets.length, 0);
  const panelId = `day-${workout.date}`;
  // At most one of these can exist, which is what makes the glow on it affordable.
  const isToday = workout.date === today;

  return (
    <li className={`day${isToday ? " today" : ""}${lit ? " lit" : ""}`}>
      <button className="day-summary" type="button" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
        <span className="day-chevron" aria-hidden="true" />
        <span className="day-when">{relativeDay(workout.date, today)}</span>
        <span className="day-counts">
          {workout.exercises.length} {plural(workout.exercises.length, "exercise")} · {sets}{" "}
          {plural(sets, "set")}
        </span>
        <span className="day-score num">{formatNumber(score)}</span>
        {/* The bar is decoration for a sighted reader only; the number above is the data. */}
        <span className="day-bar" aria-hidden="true">
          <span style={{ width: `${Math.max(share * 100, 1.5)}%` }} />
        </span>
      </button>

      {open && (
        <div className="day-detail fade-in" id={panelId}>
          {workout.exercises.map((exercise) => {
            const entry = exerciseById(exercise.catalogId);
            const measure = entry?.measure ?? "reps";
            // "supported" on a whole exercise would be a lie when only the last set was.
            const supported = exercise.sets.filter((s) => s.supported).length;
            return (
              <div className="exercise" key={exercise.id}>
                <span className="exercise-name">{entry?.name ?? exercise.name}</span>
                <span className="exercise-sets">
                  {formatSets({ measure }, exercise.sets)}
                  {supported > 0 && (
                    <span className="tag-supported">
                      {" · "}
                      {supported === exercise.sets.length
                        ? "supported"
                        : `${supported} of ${exercise.sets.length} supported`}
                    </span>
                  )}
                </span>
                <span className="exercise-score num">
                  {formatNumber(scoreWorkout({ ...workout, exercises: [exercise] }))}
                </span>
              </div>
            );
          })}
          <p className="sub day-foot">
            Logged at {workout.bodyweightKg}kg bodyweight, which is what this day is scored
            against.
          </p>
          {/* Its own control rather than a link inside the sentence: it is the action
              this panel exists to offer, and an inline link cannot be a 44px target
              without breaking the paragraph around it. */}
          <Link
            className="btn btn-small day-edit"
            to={`/day/${workout.date}`}
            state={{ from: "progress" }}
          >
            Edit this day
          </Link>
        </div>
      )}
    </li>
  );
}
